import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { TextInput, FieldLabel, PrimaryButton } from "@/components/ui-kit";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteEmail = params.get("invite_email") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (inviteEmail && !email) setEmail(inviteEmail);
  }, [inviteEmail]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://static.prod-images.emergentagent.com/jobs/1fb72acf-ec65-4968-aa39-67a6a620319f/images/765f20325f30549979002ab5576379530d4ec61dcaa2828a1089f3871321a50a.png")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#F9F8F6]/30 to-[#1A1A1A]/40" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-[#F9F8F6]">
          <div className="text-2xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
            Atelier&nbsp;Business
          </div>
          <div className="max-w-md">
            <p className="text-4xl leading-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
              {lang === "es"
                ? "Diseña tu plan de negocio con elegancia."
                : "Design your business plan with elegance."}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center justify-end p-6 lg:p-8">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="register-form">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A8886]">
                {t("auth.register")}
              </div>
              <h1
                className="mt-3 text-4xl sm:text-5xl leading-none text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              >
                {t("auth.create_account")}
              </h1>
              <p className="mt-3 text-sm text-[#5C5B5A]">{t("auth.tagline")}</p>
              {inviteEmail && (
                <div
                  className="mt-5 border border-[#B89154]/40 bg-[#B89154]/10 text-[#5C5B5A] text-sm rounded-sm px-4 py-3"
                  data-testid="invite-banner"
                >
                  {lang === "es"
                    ? "Te han invitado a colaborar en un proyecto. Regístrate con este email y entrarás directamente al proyecto."
                    : "You've been invited to collaborate on a project. Register with this email and you'll join the project immediately."}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <FieldLabel>{t("auth.name")}</FieldLabel>
                <TextInput value={name} onChange={setName} testId="register-name-input" />
              </div>
              <div>
                <FieldLabel>{t("auth.email")}</FieldLabel>
                <TextInput
                  type="email"
                  value={email}
                  onChange={setEmail}
                  testId="register-email-input"
                  required
                />
              </div>
              <div>
                <FieldLabel>{t("auth.password")}</FieldLabel>
                <TextInput
                  type="password"
                  value={password}
                  onChange={setPassword}
                  testId="register-password-input"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-[#9E2A2B] bg-[#9E2A2B]/10 border border-[#9E2A2B]/20 rounded-sm px-4 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <PrimaryButton
              type="submit"
              disabled={loading}
              testId="register-submit-btn"
              className="w-full py-3"
            >
              {loading ? t("common.loading") : t("auth.register")}
            </PrimaryButton>

            <p className="text-sm text-[#5C5B5A]">
              {t("auth.have_account")}{" "}
              <Link
                to="/login"
                className="text-[#2563EB] underline underline-offset-4 hover:text-[#1D4ED8]"
                data-testid="login-link"
              >
                {t("auth.login")}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
